import { useEffect, useState } from "react";

import { App, theme, Typography } from "antd";
import { useTranslation } from "react-i18next";

import {
  NotificationTypeCategories,
  NotificationTypes,
} from "@/shared/utils/constants.js";
import {
  useGetNotificationSettingsForUserQuery,
  usePatchNotificationSettingsMutation,
} from "@/redux/service.js";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.tsx";
import BaseSwitch from "@/components_v2/FormFields/Switch/BaseSwitch.js";

const { Title, Text } = Typography;

const NotificationSettings = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { notification } = App.useApp();

  const {
    data: notificationSettingsData,
    isSuccess: isNotificationSettingsSuccess,
  } = useGetNotificationSettingsForUserQuery();
  const [patchNotificationSettings] = usePatchNotificationSettingsMutation();

  const [notificationSettings, setNotificationSettings] = useState(null);

  useEffect(() => {
    if (isNotificationSettingsSuccess) {
      const settings = notificationSettingsData.reduce((acc, i) => {
        acc[i.type] = {
          id: i.id,
          enabled: i.enabled,
        };
        return acc;
      }, {});
      setNotificationSettings(settings);
    }
  }, [notificationSettingsData, isNotificationSettingsSuccess]);

  const handleChangeForNotificationType = (notificationType, value) => {
    const id = notificationSettings[notificationType].id;
    patchNotificationSettings({ id, data: { enabled: value } })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("community.labels.notificationsSettingsUpdated"),
        });
      });
  };

  const handleDisableAll = (category) => {
    const promises = Object.values(NotificationTypes)
      .filter((type) => type.category === category.category)
      .map((type) => type.value)
      .filter((typeValue) => notificationSettings[typeValue]?.enabled)
      .map((typeValue) =>
        patchNotificationSettings({
          id: notificationSettings[typeValue].id,
          data: { enabled: false },
        }),
      );

    Promise.all(promises).then(() => {
      notification.success({
        message: t("community.labels.notificationsSettingsUpdated"),
      });
    });
  };

  if (!notificationSettings) return <BaseSpinner />;

  return (
    <div>
      {Object.values(NotificationTypeCategories).map((category) => {
        const orderedTypesForCategory = Object.values(NotificationTypes)
          .filter((type) => type.category === category.category)
          .map((type) => type.value)
          .filter((typeValue) => notificationSettings[typeValue]);
        const areAllDisabled = orderedTypesForCategory.every(
          (setting) => !notificationSettings[setting].enabled,
        );
        return (
          <div className="mb-4" key={category.category}>
            <div className="mb-2">
              <Title
                level={4}
                style={{ display: "inline", color: token.colorTextThird }}
                className="mb-2 me-3"
              >
                {t(category.labelKey)}
              </Title>
              <Text
                style={
                  areAllDisabled
                    ? {
                        fontSize: "15px",
                        fontWeight: 500,
                        cursor: "not-allowed",
                      }
                    : {
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "rgba(91, 146, 229, 1)",
                        cursor: "pointer",
                      }
                }
                onClick={
                  areAllDisabled ? null : () => handleDisableAll(category)
                }
              >
                {t("disableAll")}
              </Text>
            </div>

            {orderedTypesForCategory.map((settingType) => (
              <div key={settingType} className="mb-3">
                <BaseSwitch
                  checked={notificationSettings[settingType].enabled}
                  onChange={(e) =>
                    handleChangeForNotificationType(settingType, e)
                  }
                />
                <Text
                  className="ms-2"
                  style={{
                    fontWeight: "bold",
                    color: token.colorTextThird,
                    fontSize: "15px",
                  }}
                >
                  {t(NotificationTypes[settingType].labelKey)}
                </Text>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default NotificationSettings;
